import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Database, Download, RotateCcw, Play, Shield, Clock, AlertTriangle, Calendar, CheckCircle2, Image as ImageIcon } from 'lucide-react';
import { Card } from '../components/ui/card';
import Navigation from '../components/Navigation';
import { getAdminHeaders } from '../lib/auth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BackupRestore = () => {
  const navigate = useNavigate();
  const [backups, setBackups] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningBackup, setRunningBackup] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [tab, setTab] = useState('backups');
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [normalizingDates, setNormalizingDates] = useState(false);
  const [normalizeResult, setNormalizeResult] = useState(null);
  const [auditingDevocional, setAuditingDevocional] = useState(false);
  const [devocionalAudit, setDevocionalAudit] = useState(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessResult, setReprocessResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [backupsRes, snapshotsRes] = await Promise.all([
        axios.get(`${API}/admin/backups`, { headers: getAdminHeaders() }),
        axios.get(`${API}/admin/snapshots?limit=100`, { headers: getAdminHeaders() }),
      ]);
      setBackups(backupsRes.data.backups || []);
      setSnapshots(snapshotsRes.data.snapshots || []);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar dados de backup');
    } finally {
      setLoading(false);
    }
  };

  const triggerBackup = async () => {
    setRunningBackup(true);
    try {
      const res = await axios.post(`${API}/admin/backups/run`, {}, { headers: getAdminHeaders() });
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error('Erro ao executar backup');
    } finally {
      setRunningBackup(false);
    }
  };

  const handleNormalizeDates = async () => {
    if (!window.confirm('Isto percorrera todas as criancas e ajustara datas de nascimento no formato ISO. Continuar?')) return;
    setNormalizingDates(true);
    setNormalizeResult(null);
    try {
      const res = await axios.post(`${API}/admin/cleanup/normalize-dates`, {}, { headers: getAdminHeaders() });
      setNormalizeResult(res.data);
      toast.success(`${res.data.corrigidas} data(s) normalizada(s), ${res.data.ja_ok} ja estavam OK`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao normalizar datas');
    } finally {
      setNormalizingDates(false);
    }
  };

  const handleAuditDevocional = async (fix = false) => {
    if (fix && !window.confirm('Isto vai AJUSTAR os registros semanais que violam o limite de 14 sementes/semana. Continuar?')) return;
    setAuditingDevocional(true);
    try {
      const res = await axios.post(`${API}/admin/cleanup/audit-devocional?dry_run=${!fix}`, {}, { headers: getAdminHeaders() });
      setDevocionalAudit(res.data);
      if (fix && res.data.corrigidas > 0) toast.success(`${res.data.corrigidas} registro(s) corrigido(s)`);
      else if (res.data.violacoes === 0) toast.success('Nenhuma violacao encontrada!');
      else toast.info(`${res.data.violacoes} violacao(oes) detectada(s). Clique em Corrigir para ajustar.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro na auditoria');
    } finally {
      setAuditingDevocional(false);
    }
  };

  const handleReprocessPhotos = async () => {
    if (!window.confirm('Isto vai reprocessar TODAS as fotos antigas: aplicar EXIF, crop quadrado 512x512 e re-comprimir como JPEG. Pode demorar alguns minutos. Continuar?')) return;
    setReprocessing(true);
    setReprocessResult(null);
    try {
      const res = await axios.post(`${API}/admin/cleanup/reprocess-photos`, {}, { headers: getAdminHeaders(), timeout: 300000 });
      setReprocessResult(res.data);
      toast.success(`${res.data.reprocessadas} foto(s) reprocessada(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao reprocessar fotos');
    } finally {
      setReprocessing(false);
    }
  };

  const restoreFullBackup = async (backupId) => {
    setRestoringId(backupId);
    try {
      const res = await axios.post(`${API}/admin/backups/${backupId}/restore`, {}, { headers: getAdminHeaders() });
      toast.success(res.data.message);
      setConfirmRestore(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao restaurar backup');
    } finally {
      setRestoringId(null);
    }
  };

  const restoreSnapshot = async (entityType, entityId) => {
    setRestoringId(`${entityType}-${entityId}`);
    try {
      const res = await axios.post(`${API}/admin/snapshots/${entityType}/${entityId}/restore`, {}, { headers: getAdminHeaders() });
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao restaurar snapshot');
    } finally {
      setRestoringId(null);
    }
  };

  const formatDate = (ts) => {
    try {
      return new Date(ts).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return ts; }
  };

  const entityLabels = {
    crianca: 'Criança',
    atividade_diaria: 'Ativ. Diaria',
    atividade_semanal: 'Ativ. Semanal',
    voluntario: 'Voluntário',
    grupo: 'Grupo',
    usuario: 'Usuario',
    visitante: 'Visitante',
    pais: 'Pais/Responsável',
  };

  const actionLabels = {
    editar: 'Edição',
    excluir: 'Exclusão',
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      {/* Header */}
      <div className="bg-[#1A4D2E] text-white px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(-1)} data-testid="backup-back-btn" className="hover:bg-white/10 rounded-full p-2 transition-all mb-2">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <Shield className="w-7 h-7" />
            <h1 className="font-nunito font-bold text-3xl">Backup e Restauracao</h1>
          </div>
          <p className="font-inter text-white/70 text-sm mt-1">Gerencie backups e restaure dados com seguranca</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Quick Action */}
        <Card className="bg-white rounded-2xl p-5 shadow-md border border-[#E1ECE5]">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-[#F2F7F4] p-3 rounded-xl">
                <Database className="w-6 h-6 text-[#1A4D2E]" />
              </div>
              <div>
                <h3 className="font-nunito font-bold text-[#1A4D2E]">Backup Manual</h3>
                <p className="font-inter text-sm text-gray-500">Salvar snapshot completo do banco de dados</p>
              </div>
            </div>
            <button
              data-testid="trigger-backup-btn"
              onClick={triggerBackup}
              disabled={runningBackup}
              className="flex items-center gap-2 bg-[#1A4D2E] text-white px-5 py-2.5 rounded-full font-inter font-semibold text-sm hover:bg-[#145A32] transition-all active:scale-95 disabled:opacity-50"
            >
              {runningBackup ? (
                <><RotateCcw className="w-4 h-4 animate-spin" /> Salvando...</>
              ) : (
                <><Play className="w-4 h-4" /> Executar Backup</>
              )}
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-400 font-inter">
            <Clock className="w-3 h-3" />
            <span>Backups automaticos diarios. Retencao: 90 dias.</span>
          </div>
        </Card>

        {/* Maintenance: Normalize dates */}
        <Card className="bg-white rounded-2xl p-5 shadow-md border border-[#E1ECE5]" data-testid="normalize-dates-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-amber-50 p-3 rounded-xl">
                <Calendar className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-nunito font-bold text-[#1A4D2E]">Normalizar Datas de Nascimento</h3>
                <p className="font-inter text-sm text-gray-500">Converte datas legadas (DD/MM/YYYY e variações) para o formato ISO</p>
              </div>
            </div>
            <button
              data-testid="normalize-dates-btn"
              onClick={handleNormalizeDates}
              disabled={normalizingDates}
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-5 py-2.5 rounded-full font-inter font-semibold text-sm transition-all active:scale-95 disabled:opacity-50"
            >
              {normalizingDates ? (
                <><RotateCcw className="w-4 h-4 animate-spin" /> Processando...</>
              ) : (
                <><Calendar className="w-4 h-4" /> Executar</>
              )}
            </button>
          </div>
          {normalizeResult && (
            <div className="mt-4 space-y-2" data-testid="normalize-result">
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <p className="font-nunito font-bold text-lg text-gray-700">{normalizeResult.total}</p>
                  <p className="text-[10px] text-gray-500 font-semibold">Total</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2 text-center">
                  <p className="font-nunito font-bold text-lg text-green-700">{normalizeResult.corrigidas}</p>
                  <p className="text-[10px] text-green-600 font-semibold">Corrigidas</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 text-center">
                  <p className="font-nunito font-bold text-lg text-blue-700">{normalizeResult.ja_ok}</p>
                  <p className="text-[10px] text-blue-600 font-semibold">Ja estavam OK</p>
                </div>
                <div className="bg-red-50 rounded-lg p-2 text-center">
                  <p className="font-nunito font-bold text-lg text-red-700">{normalizeResult.fallback}</p>
                  <p className="text-[10px] text-red-600 font-semibold">Nao inferidas</p>
                </div>
              </div>
              {normalizeResult.detalhes && normalizeResult.detalhes.length > 0 && (
                <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">Crianca</th>
                        <th className="px-2 py-1 text-left">De</th>
                        <th className="px-2 py-1 text-left">Para</th>
                      </tr>
                    </thead>
                    <tbody>
                      {normalizeResult.detalhes.map((d, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-2 py-1 font-semibold text-[#1A4D2E]">{d.nome}</td>
                          <td className="px-2 py-1 text-gray-500 font-mono">{d.de || '(vazio)'}</td>
                          <td className="px-2 py-1 font-mono">
                            {d.para ? (
                              <span className="text-green-600 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {d.para}</span>
                            ) : (
                              <span className="text-red-500">{d.observacao || 'Nao inferida'}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <p className="mt-3 text-xs text-gray-400">Opera ideal apos restaurar backups ou importar dados de outro sistema. Seguro: so sobrescreve quando consegue inferir a data. Datas nao inferidas sao mantidas como estao.</p>
        </Card>

        {/* Maintenance: Audit devocional cap */}
        <Card className="bg-white rounded-2xl p-5 shadow-md border border-[#E1ECE5]" data-testid="audit-devocional-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-purple-50 p-3 rounded-xl">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-nunito font-bold text-[#1A4D2E]">Auditar Limite de Devocional</h3>
                <p className="font-inter text-sm text-gray-500">Detecta e corrige semanas onde total ultrapassa 14 sementes (2pts/dia × 7 dias)</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="audit-devocional-scan-btn"
                onClick={() => handleAuditDevocional(false)}
                disabled={auditingDevocional}
                className="flex items-center gap-2 bg-white border-2 border-purple-500 text-purple-600 hover:bg-purple-50 px-4 py-2 rounded-full font-inter font-semibold text-sm transition-all disabled:opacity-50"
              >
                {auditingDevocional ? <><RotateCcw className="w-4 h-4 animate-spin" /> ...</> : <>Escanear</>}
              </button>
              {devocionalAudit && devocionalAudit.violacoes > 0 && (
                <button
                  data-testid="audit-devocional-fix-btn"
                  onClick={() => handleAuditDevocional(true)}
                  disabled={auditingDevocional}
                  className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-full font-inter font-semibold text-sm transition-all disabled:opacity-50"
                >
                  Corrigir {devocionalAudit.violacoes}
                </button>
              )}
            </div>
          </div>
          {devocionalAudit && (
            <div className="mt-4" data-testid="audit-result">
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <p className="font-nunito font-bold text-lg text-gray-700">{devocionalAudit.cap}</p>
                  <p className="text-[10px] text-gray-500 font-semibold">Cap / semana</p>
                </div>
                <div className={`${devocionalAudit.violacoes > 0 ? 'bg-red-50' : 'bg-green-50'} rounded-lg p-2 text-center`}>
                  <p className={`font-nunito font-bold text-lg ${devocionalAudit.violacoes > 0 ? 'text-red-700' : 'text-green-700'}`}>{devocionalAudit.violacoes}</p>
                  <p className={`text-[10px] font-semibold ${devocionalAudit.violacoes > 0 ? 'text-red-600' : 'text-green-600'}`}>Violacoes</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 text-center">
                  <p className="font-nunito font-bold text-lg text-blue-700">{devocionalAudit.corrigidas}</p>
                  <p className="text-[10px] text-blue-600 font-semibold">Corrigidas</p>
                </div>
              </div>
              {devocionalAudit.detalhes && devocionalAudit.detalhes.length > 0 && (
                <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">Crianca</th>
                        <th className="px-2 py-1 text-left">Semana</th>
                        <th className="px-2 py-1 text-left">Diario</th>
                        <th className="px-2 py-1 text-left">Semanal atual</th>
                        <th className="px-2 py-1 text-left">Sugerido</th>
                      </tr>
                    </thead>
                    <tbody>
                      {devocionalAudit.detalhes.map((d, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-2 py-1 font-semibold text-[#1A4D2E]">{d.child_name}</td>
                          <td className="px-2 py-1 text-gray-500 font-mono">{d.semana}</td>
                          <td className="px-2 py-1">{d.daily_count}x ({d.daily_pts}pts)</td>
                          <td className="px-2 py-1 text-red-600 font-mono">{d.weekly_pts_atual}</td>
                          <td className="px-2 py-1 text-green-600 font-mono">{d.weekly_pts_sugerido}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <p className="mt-3 text-xs text-gray-400">A correcao reduz o campo <code>devocional</code> dos registros semanais para respeitar o limite. Dados diarios permanecem intactos.</p>
        </Card>

        {/* Maintenance: Reprocess photos */}
        <Card className="bg-white rounded-2xl p-5 shadow-md border border-[#E1ECE5]" data-testid="reprocess-photos-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-pink-50 p-3 rounded-xl">
                <ImageIcon className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <h3 className="font-nunito font-bold text-[#1A4D2E]">Reprocessar Fotos Antigas</h3>
                <p className="font-inter text-sm text-gray-500">Aplica EXIF + crop quadrado 512x512 + JPEG 88% em todas as fotos cadastradas</p>
              </div>
            </div>
            <button
              data-testid="reprocess-photos-btn"
              onClick={handleReprocessPhotos}
              disabled={reprocessing}
              className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white px-5 py-2.5 rounded-full font-inter font-semibold text-sm transition-all active:scale-95 disabled:opacity-50"
            >
              {reprocessing ? <><RotateCcw className="w-4 h-4 animate-spin" /> Processando...</> : <><ImageIcon className="w-4 h-4" /> Executar</>}
            </button>
          </div>
          {reprocessResult && (
            <div className="mt-4" data-testid="reprocess-result">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
                <div className="bg-gray-50 rounded-lg p-2 text-center">
                  <p className="font-nunito font-bold text-lg text-gray-700">{reprocessResult.total}</p>
                  <p className="text-[10px] text-gray-500 font-semibold">Total</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2 text-center">
                  <p className="font-nunito font-bold text-lg text-green-700">{reprocessResult.reprocessadas}</p>
                  <p className="text-[10px] text-green-600 font-semibold">Reprocessadas</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 text-center">
                  <p className="font-nunito font-bold text-lg text-blue-700">{reprocessResult.ja_otimizadas}</p>
                  <p className="text-[10px] text-blue-600 font-semibold">Ja otimizadas</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-2 text-center">
                  <p className="font-nunito font-bold text-lg text-amber-700">{reprocessResult.ausentes}</p>
                  <p className="text-[10px] text-amber-600 font-semibold">Ausentes</p>
                </div>
                <div className="bg-red-50 rounded-lg p-2 text-center">
                  <p className="font-nunito font-bold text-lg text-red-700">{reprocessResult.falhas}</p>
                  <p className="text-[10px] text-red-600 font-semibold">Falhas</p>
                </div>
              </div>
              {reprocessResult.detalhes && reprocessResult.detalhes.filter(d => d.status !== 'ok').length > 0 && (
                <details className="border border-gray-100 rounded-lg">
                  <summary className="px-3 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-50">Ver problemas ({reprocessResult.detalhes.filter(d => d.status !== 'ok').length})</summary>
                  <div className="max-h-48 overflow-y-auto p-2 text-xs space-y-1">
                    {reprocessResult.detalhes.filter(d => d.status !== 'ok').map((d, i) => (
                      <div key={i} className="flex items-start gap-2 py-1">
                        <span className="font-semibold text-gray-700 flex-1">{d.nome}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${d.status === 'erro' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{d.status}</span>
                        {d.erro && <span className="text-red-500 font-mono text-[10px] truncate max-w-[40%]" title={d.erro}>{d.erro}</span>}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
          <p className="mt-3 text-xs text-gray-400">Seguro: pula fotos ja otimizadas e ausentes. Apaga arquivos antigos somente apos sucesso da nova gravacao.</p>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2">
          <button
            data-testid="tab-backups"
            onClick={() => setTab('backups')}
            className={`px-4 py-2 rounded-full text-sm font-inter font-semibold transition-all ${
              tab === 'backups' ? 'bg-[#1A4D2E] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Backups Completos ({backups.length})
          </button>
          <button
            data-testid="tab-snapshots"
            onClick={() => setTab('snapshots')}
            className={`px-4 py-2 rounded-full text-sm font-inter font-semibold transition-all ${
              tab === 'snapshots' ? 'bg-[#1A4D2E] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Snapshots ({snapshots.length})
          </button>
        </div>

        {loading ? (
          <p className="text-gray-500 text-center py-8 font-inter">Carregando...</p>
        ) : tab === 'backups' ? (
          /* Backups List */
          backups.length === 0 ? (
            <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
              <Database className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="font-inter text-gray-500">Nenhum backup encontrado</p>
              <p className="font-inter text-gray-400 text-sm mt-1">Execute um backup manual ou aguarde o proximo backup automatico</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {backups.map((b) => (
                <Card key={b.backup_id} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5] hover:shadow-md transition-all">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-[#F2F7F4] p-2.5 rounded-lg">
                        <Download className="w-5 h-5 text-[#1A4D2E]" />
                      </div>
                      <div>
                        <p className="font-nunito font-bold text-[#1A4D2E] text-sm">{b.backup_id}</p>
                        <div className="flex items-center gap-3 text-xs text-gray-500 font-inter mt-0.5">
                          <span>{b.total_docs} documentos</span>
                          <span>{b.collections} colecoes</span>
                          <span>{formatDate(b.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    {confirmRestore === b.backup_id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-600 font-inter font-semibold">Confirmar?</span>
                        <button
                          data-testid={`confirm-restore-${b.backup_id}`}
                          onClick={() => restoreFullBackup(b.backup_id)}
                          disabled={restoringId === b.backup_id}
                          className="bg-red-600 text-white px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-red-700 transition-all disabled:opacity-50"
                        >
                          {restoringId === b.backup_id ? 'Restaurando...' : 'Sim'}
                        </button>
                        <button
                          onClick={() => setConfirmRestore(null)}
                          className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-gray-300 transition-all"
                        >
                          Nao
                        </button>
                      </div>
                    ) : (
                      <button
                        data-testid={`restore-backup-${b.backup_id}`}
                        onClick={() => setConfirmRestore(b.backup_id)}
                        className="flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200 px-3 py-1.5 rounded-full text-xs font-inter font-semibold hover:bg-amber-100 transition-all"
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> Restaurar
                      </button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )
        ) : (
          /* Snapshots List */
          snapshots.length === 0 ? (
            <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
              <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="font-inter text-gray-500">Nenhum snapshot registrado</p>
              <p className="font-inter text-gray-400 text-sm mt-1">Snapshots sao criados automaticamente antes de edicoes e exclusoes</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {snapshots.map((s, idx) => {
                const key = `${s.entity_type}-${s.entity_id}`;
                const isRestoring = restoringId === key;
                return (
                  <Card key={idx} className={`rounded-xl p-4 shadow-sm border transition-all ${
                    s.action === 'excluir' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'
                  }`}>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            s.action === 'excluir' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {actionLabels[s.action] || s.action}
                          </span>
                          <span className="bg-white/80 px-2 py-0.5 rounded text-xs font-semibold text-gray-600">
                            {entityLabels[s.entity_type] || s.entity_type}
                          </span>
                        </div>
                        <p className="font-inter text-sm text-gray-700 mt-1 truncate">
                          {s.data?.nome || s.data?.nome_visitante || s.entity_id}
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 font-inter">
                          <span>{s.admin_email}</span>
                          <span>{formatDate(s.created_at)}</span>
                        </div>
                      </div>
                      <button
                        data-testid={`restore-snapshot-${idx}`}
                        onClick={() => restoreSnapshot(s.entity_type, s.entity_id)}
                        disabled={isRestoring}
                        className="flex items-center gap-1.5 bg-white text-[#1A4D2E] border border-[#1A4D2E]/20 px-3 py-1.5 rounded-full text-xs font-inter font-semibold hover:bg-[#F2F7F4] transition-all disabled:opacity-50"
                      >
                        <RotateCcw className={`w-3.5 h-3.5 ${isRestoring ? 'animate-spin' : ''}`} />
                        {isRestoring ? 'Restaurando...' : 'Restaurar'}
                      </button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )
        )}

        {/* Info Card */}
        <Card className="bg-amber-50 rounded-2xl p-4 border border-amber-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-nunito font-bold text-amber-800 text-sm">Atencao</p>
              <ul className="font-inter text-xs text-amber-700 mt-1 space-y-1">
                <li>Restaurar um backup completo substitui TODOS os dados atuais.</li>
                <li>Snapshots restauram apenas o registro individual.</li>
                <li>Backups sao mantidos por 90 dias.</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
      <Navigation active="inicio" />
    </div>
  );
};

export default BackupRestore;
